import {
    isToggleOn,
    jsonBytes,
    PulseAttribute,
    PulseAttributeHint,
    PulseBatch,
    PulseBatchChunk,
    PulseBatchConfig,
    PulseEvent,
    PulseMessage,
    TrackerConfigurations,
} from '@pulse/common';

const MAX_REQUEST_BYTES = 60_000;

export function getBatches(list: PulseMessage[], batchConfig: PulseBatchConfig): PulseBatch[] {
    // get copy of array to not mutate original
    const messages = [...list];
    const batches: PulseBatch[] = [];
    let lastBatchStart = 0;

    for (let i = 0; i < messages.length; i++) {
        const currMessageChunk = groupByContext([messages[i]], batchConfig);

        // If single message chunk is greater than max limit of keepalive bytes send it seperated without keepalive
        const currentChunkSize = jsonBytes<{ chunks: PulseBatchChunk[] }>({
            chunks: currMessageChunk,
        });

        if (currentChunkSize > MAX_REQUEST_BYTES) {
            batches.push({
                chunks: currMessageChunk,
                keepAlive: false,
                size: currentChunkSize,
            });
            messages.splice(i, 1);
            i = i - 1;
            continue;
        }

        const nextMessages = messages.slice(lastBatchStart, i + 1);
        const nextChunks = groupByContext(nextMessages, batchConfig);
        const nextBatchSize = jsonBytes<PulseBatchChunk[]>(nextChunks);
        const nextBatch = { chunks: nextChunks, keepAlive: true, size: nextBatchSize };

        // Batches should be smaller than 64KB to be
        // able to transferred using keepalive requests.
        // We will keep it under 60KB just to be sure.
        const sizeLimitExceeded = nextBatchSize > MAX_REQUEST_BYTES;

        if (sizeLimitExceeded) {
            pushMessagesIntoBatch(messages.slice(lastBatchStart, i), batches, batchConfig);
            lastBatchStart = i;

            // In case the last event exceeds the limit to not lose the event create separate batch
            if (i === messages.length - 1) {
                pushMessagesIntoBatch(messages.slice(lastBatchStart, i + 1), batches, batchConfig);
            }

            continue;
        }

        if (i === messages.length - 1) {
            batches.push(nextBatch);
        }
    }

    return batches;
}

function pushMessagesIntoBatch(
    messages: PulseMessage[],
    batches: PulseBatch[],
    batchConfig: PulseBatchConfig,
) {
    const nextChunks = groupByContext(messages, batchConfig);
    const nextBatchSize = jsonBytes<PulseBatchChunk[]>(nextChunks);
    const nextBatch = { chunks: nextChunks, keepAlive: true, size: nextBatchSize };
    batches.push(nextBatch);
}

/**
 * Group messages by context
 */
export function groupByContext(
    messages: PulseMessage[],
    batchConfig: PulseBatchConfig,
): PulseBatchChunk[] {
    //
    // Every time a global header field changes,
    // a new context object is being created.
    //
    // When an event is being fired, the queue will store the current
    // context alongside the fired event.
    //
    // Here we need to group messages by context references to
    // avoid duplicating the same context multiple times in a same request.
    //
    const chunkMap = new Map<Record<string, unknown>, PulseMessage[]>();

    for (const msg of messages) {
        let chunk = chunkMap.get(msg.state);

        if (!chunk) {
            chunk = [];
            chunkMap.set(msg.state, chunk);
        }

        chunk.push(msg);
    }

    return Array.from(chunkMap).map((chunk): PulseBatchChunk => {
        const events: PulseEvent[] = [];
        const attributes: PulseAttribute[] = [];
        let attributeHints: PulseAttributeHint | undefined;

        chunk[1].map((message) => {
            switch (message.type) {
                case 'event':
                    events.push(message.data);
                    break;
                case 'attribute':
                    attributes.push(message.data);
                    break;
                case 'attribute-hint':
                    attributeHints = message.data;
                    break;
            }
        });

        return {
            config: batchConfig,
            header: chunk[0],
            events,
            attributes,
            attributeHints,
        };
    });
}

export function getTrackerBatchConfig(
    config: TrackerConfigurations,
    enabled = true,
): PulseBatchConfig {
    const deviceIdEnabled = isToggleOn(config.stateEnrichment, 'device_id');
    const sessionIdEnabled = isToggleOn(config.stateEnrichment, 'session_id');
    const tabIdEnabled = isToggleOn(config.eventEnrichment, 'tab_id');
    const serverCookies = enabled ? config.serverCookies : false;

    return {
        deviceId: deviceIdEnabled ? config.sessionStorageType : 'none',
        sessionId: sessionIdEnabled ? config.sessionStorageType : 'none',
        tabId: tabIdEnabled ? config.tabManagerStorageType : 'none',
        serverCookies,
    };
}
